import type { DotNetObjectReference } from '../types';

/**
 * Interface für das StickyBottom-Modul
 */
export interface IStickyBottomModule {
    /**
     * Initialisiert den Sticky-Observer für ein Element
     * @param element - Das zu beobachtende Element
     * @param dotNetReference - .NET-Objektreferenz für Callbacks
     */
    initStickyObserver(element: HTMLElement, dotNetReference: DotNetObjectReference): void;

    /**
     * Prüft ob padding-right gesetzt werden soll basierend auf vorhandenen Elementen
     * @returns '4rem' wenn help-button-placer existiert und side-panel nicht, sonst null
     */
    checkPaddingRequirement(): string | null;

    /**
     * Gibt Ressourcen frei und beendet die Beobachtung
     */
    dispose(): void;
}

// Private Modul-Variablen
let observerInstance: IntersectionObserver | null = null;
let dotNetRef: DotNetObjectReference | null = null;
let sentinel: HTMLElement | null = null;

/**
 * Modul für Sticky-Bottom-Content-Funktionalität
 * Erkennt, ob ein sticky-positioniertes Element am unteren Rand "klebt"
 */
const StickyBottomModule: IStickyBottomModule = {
    /**
     * Initialisiert den Sticky-Observer für ein Element
     * @param element - Das zu beobachtende sticky-Element
     * @param dotNetReference - .NET-Objektreferenz für Callbacks
     */
    initStickyObserver(element: HTMLElement, dotNetReference: DotNetObjectReference): void {
        dotNetRef = dotNetReference;

        // Erstelle ein Sentinel-Element zur Erkennung des Sticky-Status
        sentinel = document.createElement('div');
        sentinel.style.position = 'absolute';
        sentinel.style.bottom = '0';
        sentinel.style.height = '1px';
        sentinel.style.width = '1px';
        sentinel.style.pointerEvents = 'none';
        sentinel.setAttribute('data-sticky-sentinel', 'true');

        const parent = element.parentElement;
        if (!parent) {
            console.warn('StickyBottomModule: Element has no parent');
            return;
        }

        // Stelle sicher, dass das Parent-Element relative positioniert ist
        const parentPosition = window.getComputedStyle(parent).position;
        if (parentPosition === 'static') {
            parent.style.position = 'relative';
        }

        parent.appendChild(sentinel);

        // Erstelle den Intersection Observer
        observerInstance = new IntersectionObserver(
            ([entry]) => {
                // Wenn das Sentinel nicht sichtbar ist, ist das Element sticky
                const isSticky = !entry.isIntersecting;
                
                if (dotNetRef) {
                    dotNetRef.invokeMethodAsync('UpdateStickyState', isSticky);
                }
            },
            {
                threshold: [1],
                root: null
            }
        );

        observerInstance.observe(sentinel);
    },

    /**
     * Prüft ob padding-right gesetzt werden soll basierend auf vorhandenen Elementen
     * @returns '4rem' wenn help-button-placer existiert und side-panel nicht, sonst null
     */
    checkPaddingRequirement(): string | null {
        const hasHelpButton = document.querySelector('.help-button-placer') !== null;
        const hasSidePanel = document.querySelector('.side-panel') !== null;
        
        if (hasHelpButton && !hasSidePanel) {
            return '4rem';
        }
        
        return null;
    },

    /**
     * Gibt Ressourcen frei und beendet die Beobachtung
     */
    dispose(): void {
        if (observerInstance) {
            observerInstance.disconnect();
            observerInstance = null;
        }

        if (sentinel && sentinel.parentElement) {
            sentinel.parentElement.removeChild(sentinel);
            sentinel = null;
        }

        dotNetRef = null;
    }
};

// Exportiere die Funktionen für JSInterop
export function initStickyObserver(element: HTMLElement, dotNetReference: DotNetObjectReference): void {
    StickyBottomModule.initStickyObserver(element, dotNetReference);
}

export function dispose(): void {
    StickyBottomModule.dispose();
}

export default StickyBottomModule;
